import { ChangeDetectorRef, Component, Input, Output, EventEmitter, ElementRef, HostListener, ViewChild, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { ReactiveBadgeComponent } from "../reactive-badge/reactive-badge.component";
import { LookupItem } from '../../../services/LookupService';
import { StringHelpers } from '../../../helpers/StringHelpers';
import { IStaticStylesConfig } from '../../../skins/_primitives/IStaticStylesConfig';
import { MatwinsV1 } from '../../../skins/global.skin';

export interface SelectOption {
  key: string;
  value: string;
}

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ReactiveBadgeComponent],
  templateUrl: './multi-select.component.html',
  host: { class: 'block' }
})
export class MultiSelectComponent {
  @Input() isMultiselect: boolean = true;
  @Input() options: SelectOption[] = [];
  @Input() placeholder: string = 'Select items';
  @Input() maxItems: number | null = null;    // null = infini
  @Input() selectedKeys: string[] = [];
  @Input() isReactive: boolean = false;
  @Input() skin: IStaticStylesConfig | any = MatwinsV1;
  @Input() reactiveIconAdditionalDataName: string = 'truckerresponsibleicon';
  @Input() minChars: number = 0;
  @Input() allowCreate: boolean = false;


  @Output() selectedKeysChange = new EventEmitter<string[]>();
  @Output() statusChange = new EventEmitter<{ key: string, status: number }>();
  @Output() createNew = new EventEmitter<string>();

  isOpen = false;
  searchTerm = '';

  /**
   * id/name unique par instance : Chrome associe son historique de saisie au
   * name du champ et affiche son popup d'autofill par-dessus la liste
   * d'options (meme avec autocomplete="new-password"). Un nom aleatoire le
   * prive de tout historique a suggerer.
   */
  readonly searchFieldName = `ms-search-${Math.random().toString(36).slice(2)}`;

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  /** Ouvre le dropdown sans le refermer (contrairement a toggleDropdown). */
  openDropdown(): void {
    if (!this.isOpen) {
      this.toggleDropdown();
    }
  }

  // Methode pour recuperer le libelle a partir de la key
  getLabel(key: string): string {
    const option = this.options.find(o => o.key === key);
    return option ? option.value : key;
  }

  getChildren(key: string): LookupItem[] {
    const option = this.options.find(o => o.key === key);
    return option ? (option as LookupItem)?.children || [] : [];
  }

  getSelectedValue(key: string): number {
    const option = this.options.find(o => o.key === key);
    return option ? (option as LookupItem)?.selectedValue || 0 : 0;
  }

  // Filtrage
  get filteredOptions(): SelectOption[] {
    if (this.minChars > 0 && this.searchTerm.length < this.minChars) return [];

    if (!this.searchTerm) return this.options;

    const term = StringHelpers.NormalizeForSearch(this.searchTerm);
    return this.options.filter(opt => StringHelpers.NormalizeForSearch(opt.value).includes(term));
  }

  // Texte affiche dans le champ principal
  get displayText(): string {
    return `${this.selectedKeys.length}`;
  }

  get labelForSingleSelect(): string {
    return this.getLabel(this.selectedKeys[0]);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
      // Rendu + focus synchrones : sur iOS/iPad, le clavier ne s'ouvre que si
      // focus() se produit dans le contexte du geste utilisateur (un setTimeout
      // le ferait sortir du geste et le focus serait ignore).
      this.cdr.detectChanges();
      this.searchInput?.nativeElement.focus();
    }
  }

  selectOption(option: SelectOption) {
    if (this.isMultiselect === false && this.selectedKeys.length > 0) {
      this.selectedKeys = [option.key];
      // this.selectedKeysChange.emit(this.selectedKeys);
      // return;
    }
    else {
      if (this.selectedKeys.includes(option.key)) return;
      if (this.maxItems && this.selectedKeys.length >= this.maxItems) return;
      this.selectedKeys = [...this.selectedKeys, option.key];
    }
    if (this.maxItems && this.maxItems === 1) {
      this.toggleDropdown();
    }
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  removeTag(key: string) {
    this.selectedKeys = this.selectedKeys.filter(k => k !== key);
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  onCreateNew(): void {
    if (this.searchTerm.trim()) {
      const name = this.searchTerm.trim();
      const tempKey = `__new__${name}`;

      // Ajouter une option temporaire pour afficher le label
      this.options = [...this.options, { key: tempKey, value: name }];

      // Sélectionner cette option
      if (this.isMultiselect === false) {
        this.selectedKeys = [tempKey];
      } else {
        this.selectedKeys = [...this.selectedKeys, tempKey];
      }
      this.selectedKeysChange.emit(this.selectedKeys);

      this.createNew.emit(name);
      this.searchTerm = '';
      this.isOpen = false;
    }
  }



  // Pour cacher le drop-down
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) { }
}
